import type { FamilyFeudAction } from '@cctv/pages/Block/FamilyFeudManager/familyFeudReducer';
import type { SoundKey } from '@cctv/sounds';

// ===== CORE DOMAIN TYPES =====

/** A value as it survives JSON transport between the Rails API and the client. */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/** A JSON object whose keys are decided by the owning block kind rather than this contract. */
export type JsonObject = { [key: string]: JsonValue };

export interface AuthError extends Error {
  code?: number;
}

export type UserRole = 'user' | 'admin' | 'superadmin';
export type ExperienceStatus = 'draft' | 'lobby' | 'live' | 'paused' | 'finished' | 'archived';
export type ParticipantRole = 'audience' | 'player' | 'moderator' | 'host';
export type ParticipantStatus = 'registered' | 'active';
export type BlockStatus = 'hidden' | 'open' | 'closed';
export enum BlockKind {
  POLL = 'poll',
  QUESTION = 'question',
  ANNOUNCEMENT = 'announcement',
  FAMILY_FEUD = 'family_feud',
  PHOTO_UPLOAD = 'photo_upload',
  BUZZER = 'buzzer',
  GUESS_WHO = 'guess_who',
  MINIGAME_ARITHMETIC = 'minigame_arithmetic',
  MINIGAME_BALLOON_PUMP = 'minigame_balloon_pump',
  COLLABORATIVE_DRAWING = 'collaborative_drawing',
  THE_SCENE = 'the_scene',
  FEEDBACK = 'feedback',
  NEWSLETTER_SIGNUP = 'newsletter_signup',
}

export const BLOCK_KIND_LABELS = {
  [BlockKind.POLL]: 'Poll',
  [BlockKind.QUESTION]: 'Question',
  [BlockKind.ANNOUNCEMENT]: 'Announcement',
  [BlockKind.FAMILY_FEUD]: 'Family Feud',
  [BlockKind.PHOTO_UPLOAD]: 'Photo Upload',
  [BlockKind.BUZZER]: 'Buzzer',
  [BlockKind.GUESS_WHO]: 'Guess Who',
  [BlockKind.MINIGAME_ARITHMETIC]: 'Minigame: Arithmetic',
  [BlockKind.MINIGAME_BALLOON_PUMP]: 'Minigame: Balloon Pump',
  [BlockKind.COLLABORATIVE_DRAWING]: 'Collaborative Drawing',
  [BlockKind.THE_SCENE]: 'The Scene',
  [BlockKind.FEEDBACK]: 'Feedback',
  [BlockKind.NEWSLETTER_SIGNUP]: 'Newsletter Signup',
} satisfies Record<BlockKind, string>;

export interface ExperienceSegment {
  id: string;
  name: string;
  color: string;
  position: number;
}

// ===== BLOCK PAYLOAD TYPES =====

export interface PollPayload {
  question: string;
  options: string[];
  pollType?: 'single' | 'multiple';
  segmentAssignments?: Record<string, string>;
}

export interface QuestionPayload {
  question: string;
  formKey: string;
  inputType?: 'text' | 'number' | 'email' | 'password' | 'tel';
  ai_context?: string;
  synthetic?: boolean;
  generate_count?: number;
  buckets?: Array<{ id: string; name: string; answer_ids?: string[] }>;
}

export interface AnnouncementPayload {
  message: string;
  show_on_monitor?: boolean;
}

/** Feedback types offered to the reporter, shared by the block and the global panel. */
export enum FeedbackType {
  BUG = 'bug',
  EXPERIENCE = 'experience',
  GENERAL = 'general',
  OTHER = 'other',
}

export const FEEDBACK_TYPE_LABELS = {
  [FeedbackType.BUG]: 'Bug',
  [FeedbackType.EXPERIENCE]: 'Experience',
  [FeedbackType.GENERAL]: 'General',
  [FeedbackType.OTHER]: 'Other',
} satisfies Record<FeedbackType, string>;

/** Where a report originated. Determines how it is grouped in Linear. */
export enum FeedbackSource {
  MANUAL = 'manual',
  ERROR = 'error',
  BLOCK = 'block',
}

export interface FeedbackPayload {
  prompt: string;
  allowed_types?: FeedbackType[];
  require_title?: boolean;
  show_on_monitor?: boolean;
}

export interface FamilyFeudAnswer {
  id: string;
  text: string;
}

export interface FamilyFeudBucket {
  bucket_id: string;
  bucket_name: string;
  percentage: number;
  revealed: boolean;
  answers?: FamilyFeudAnswer[];
}

export interface FamilyFeudQuestion {
  question_id: string;
  question_text: string;
  buckets: FamilyFeudBucket[];
}

export interface FamilyFeudGameState {
  phase: 'gathering' | 'playing';
  current_question_index: number;
  questions: FamilyFeudQuestion[];
  show_x: boolean;
}

export interface FamilyFeudPayload {
  title: string;
  ai_context?: string;
  bucket_configuration?: {
    buckets: Array<{
      id: string;
      name: string;
      answer_ids: string[];
    }>;
  };
  game_state?: FamilyFeudGameState;
  theme_music_playing?: boolean;
  theme_music_restart_count?: number;
}

export interface PhotoUploadPayload {
  prompt: string;
}

export interface BuzzerPayload {
  label?: string;
  prompt?: string;
}

export interface NewsletterSignupPayload {
  prompt?: string;
  confirmationMessage?: string;
  declineMessage?: string;
}

export interface GuessWhoClue {
  id: string;
  prompt: string;
  answer: { text?: string | null; raw?: unknown; options?: string[]; buzzed_at?: string | null };
  photo_url: string | null;
  source_block_id: string;
  block_kind: BlockKind;
  position: number;
  hidden: boolean;
}

export interface GuessWhoUserSummary {
  user_id: string;
  name: string;
  avatar?: AvatarData | null;
}

export interface GuessWhoContestant {
  contestant_user_id: string | null;
  contestant: GuessWhoUserSummary | null;
  mystery_user_id: string | null;
  mystery: GuessWhoUserSummary | null;
  clues: GuessWhoClue[];
  current_clue_index: number;
  board_candidate_ids: string[];
  eliminated_user_ids: string[];
  unanswered_user_ids: string[];
}

export type GuessWhoMonitorView =
  | 'idle'
  | 'c1_clue'
  | 'c1_board'
  | 'c2_clue'
  | 'c2_board'
  | 'reveal';

export interface GuessWhoActivePoll {
  id: string;
  options: string[];
  user_responded?: boolean;
  user_response?: { id: string; answer: { selectedOptions?: string[] } } | null;
}

export interface GuessWhoPayload {
  segment_id?: string;
  contestant_segment_id?: string;
  eligibility_threshold?: number;
  contestants: GuessWhoContestant[];
  monitor_view: GuessWhoMonitorView;
  started?: boolean;
  revealed?: boolean;
  theme_music_playing?: boolean;
  theme_music_restart_count?: number;
  active_poll_block_id?: string | null;
  active_poll_contestant_index?: 0 | 1 | null;
  active_poll?: GuessWhoActivePoll | null;
  active_poll_response_count?: number;
  active_poll_total_participants?: number;
  error?: string;
}

export interface MinigameArithmeticQuestion {
  index: number;
  lhs: number;
  op: '+' | '-' | '*' | '/';
  rhs: number;
  answer: number;
  prompt: string;
}

export interface MinigameArithmeticCurrentQuestion {
  index: number;
  prompt: string;
}

export interface MinigameArithmeticLeaderboardEntry {
  participant_id: string;
  user_id: string;
  name: string;
  avatar?: AvatarData | null;
  correct: number;
  completed: number;
  rank: number;
}

export interface MinigameArithmeticPayload {
  variant: 'arithmetic';
  duration_seconds: number;
  question_count: number;
  leaderboard_size: number;
  started_at: string | null;
  ended_at: string | null;
  questions?: MinigameArithmeticQuestion[];
  current_question?: MinigameArithmeticCurrentQuestion | null;
  score?: { correct: number; completed: number };
  leaderboard?: MinigameArithmeticLeaderboardEntry[];
  submission_count?: number;
}

export interface MinigameBalloonPumpPodiumEntry {
  place: 1 | 2 | 3;
  participant_id: string;
  name: string;
  avatar?: AvatarData | null;
  fill_amount: number;
}

export type CollaborativeDrawingPhase = 'intake' | 'round';

export interface CollaborativeDrawingPoolItem {
  photo_id: string;
  url: string;
}

// Fractional (0–1) rectangle of the source photo a slice covers. Slices tile
// the photo as an as-square-as-possible grid.
export interface CollaborativeDrawingRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CollaborativeDrawingCompositeSlice {
  slice_index: number;
  image: string | null;
  name?: string | null;
  region: CollaborativeDrawingRegion;
}

export interface CollaborativeDrawingComposite {
  group_index: number;
  slice_count: number | null;
  source_photo_url: string | null;
  slices: CollaborativeDrawingCompositeSlice[];
}

export interface CollaborativeDrawingBoardSlice {
  slice_index: number;
  participant_id: string;
  name?: string | null;
  avatar?: AvatarData | null;
  submitted: boolean;
}

export interface CollaborativeDrawingBoardGroup {
  group_index: number;
  slices: CollaborativeDrawingBoardSlice[];
}

export interface CollaborativeDrawingPayload {
  prompt: string;
  min_subsections: number;
  max_subsections: number;
  drawing_time_seconds: number;
  total_drawings: number;
  phase: CollaborativeDrawingPhase;
  // Set on the round block: the linked photo-intake block it draws its pool from.
  intake_block_id?: string | null;
  subsection_count: number | null;
  pool: CollaborativeDrawingPoolItem[];
  preview_started_at: string | null;
  round_started_at: string | null;
  ended_at: string | null;
  composites: CollaborativeDrawingComposite[] | null;
  // The host dispatches composites to the monitor; until then they only show
  // in the manage preview.
  composites_revealed?: boolean;
  // Present on monitor/manage payloads while the round runs.
  board?: CollaborativeDrawingBoardGroup[];
}

// Per-participant slice assignment, delivered via client submission state
// (never in a shared broadcast payload).
export interface CollaborativeDrawingAssignment {
  group_index: number;
  slice_index: number;
  slice_count: number;
  region: CollaborativeDrawingRegion;
  source_photo_url: string | null;
}

export type TheScenePhase = 'idle' | 'collecting' | 'winner_reveal' | 'ended';

export interface TheSceneSuggestion {
  id: string;
  text: string;
  participant_id: string;
  vote_count: number;
  rank: number;
}

export interface TheScenePerformer {
  participant_id: string;
  user_id: string;
  name: string;
  slug: string | null;
  photo_url: string | null;
  has_performer_profile: boolean;
}

export interface TheScenePayload {
  phase: TheScenePhase;
  scene_started_at: string | null;
  winner_revealed_at: string | null;
  leaderboard_size: number;
  prompt_input_count: number;
  performer_participant_ids: string[];
  prompt_participant_ids: string[];
  buzzer_participant_id: string | null;
  leaderboard: TheSceneSuggestion[];
  performers: TheScenePerformer[];
  all_suggestions?: TheSceneSuggestion[];
}

export interface MinigameBalloonPumpPayload {
  variant: 'balloon_pump';
  target_units: number;
  started_at: string | null;
  ended_at: string | null;
  leader_fill?: number;
  winner_participant_ids: string[];
  podium?: MinigameBalloonPumpPodiumEntry[];
}

export interface BlockLink {
  id: string;
  parent_block_id: string;
  child_block_id: string;
  relationship: 'depends_on';
  position: number;
}

// Individual API payload types for builder functions
export interface PollApiPayload {
  type: 'poll';
  question: string;
  options: string[];
  pollType: 'single' | 'multiple';
  segmentAssignments?: Record<string, string>;
}

export interface QuestionApiPayload {
  type: 'question';
  question: string;
  formKey: string;
  inputType: 'text' | 'number' | 'email' | 'password' | 'tel';
}

export interface AnnouncementApiPayload {
  type: 'announcement';
  message: string;
  show_on_monitor: boolean;
}

export interface FamilyFeudApiPayload {
  type: 'family_feud';
  title: string;
}

export interface PhotoUploadApiPayload {
  type: 'photo_upload';
  prompt: string;
}

export interface BuzzerApiPayload {
  type: 'buzzer';
  label?: string;
  prompt?: string;
}

export interface NewsletterSignupApiPayload {
  type: 'newsletter_signup';
  prompt?: string;
  confirmationMessage?: string;
  declineMessage?: string;
}

export interface GuessWhoApiPayload {
  type: 'guess_who';
  segment_id: string;
}

export interface MinigameArithmeticApiPayload {
  type: 'minigame_arithmetic';
  variant: 'arithmetic';
  duration_seconds: number;
  question_count: number;
  leaderboard_size: number;
}

export interface TheSceneApiPayload {
  type: 'the_scene';
  leaderboard_size: number;
  prompt_input_count: number;
  performer_participant_ids: string[];
}

export interface FeedbackApiPayload {
  type: 'feedback';
  prompt: string;
  allowed_types: FeedbackType[];
  require_title: boolean;
}

export interface MinigameBalloonPumpApiPayload {
  type: 'minigame_balloon_pump';
  variant: 'balloon_pump';
  target_units: number;
}

export interface CollaborativeDrawingApiPayload {
  type: 'collaborative_drawing';
  prompt: string;
  min_subsections: number;
  max_subsections: number;
  drawing_time_seconds: number;
  total_drawings: number;
}

// Discriminated union for API payloads (what gets sent to backend)
export type ApiPayload =
  | PollApiPayload
  | QuestionApiPayload
  | AnnouncementApiPayload
  | FamilyFeudApiPayload
  | PhotoUploadApiPayload
  | BuzzerApiPayload
  | GuessWhoApiPayload
  | MinigameArithmeticApiPayload
  | MinigameBalloonPumpApiPayload
  | CollaborativeDrawingApiPayload
  | TheSceneApiPayload
  | FeedbackApiPayload
  | NewsletterSignupApiPayload;

// ===== PLAYBILL TYPES =====

export interface PlaybillSection {
  id: string;
  performer_id: string;
  title: string;
  body: string;
  image_url?: string;
}

// ===== ENTITY TYPES =====

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
  most_recent_participant_name?: string;
  performer_slug?: string | null;
}

export interface AvatarStroke {
  points: number[];
  color: string;
  width: number;
  committed?: boolean;
}

// Clothing is placed/draggable; a frame wraps the whole avatar and is not
// manipulable.
export type CosmeticCategory = 'clothing' | 'frame';

// Placement of a cosmetic on the avatar, in the fixed 320x320 draw space.
// `asset_key` is denormalized so any renderer can resolve the art without the
// cosmetic catalog.
export interface CosmeticPlacement {
  cosmetic_id: string;
  slug: string;
  asset_key: string;
  category: CosmeticCategory;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

// A cosmetic the user owns, available to apply to their avatar.
export interface Cosmetic {
  id: string;
  name: string;
  slug: string;
  kind: string;
  category: CosmeticCategory;
  asset_key: string;
  default_placement: Pick<CosmeticPlacement, 'x' | 'y' | 'width' | 'height' | 'rotation'>;
}

// Persisted avatar. Finalized avatars store a flattened `image` plus a separate,
// non-flattened `cosmetics` layer. `strokes` is legacy (avatars drawn before the
// flatten change) and is still rendered as a fallback.
export interface AvatarData {
  image?: string;
  cosmetics?: CosmeticPlacement[];
  strokes?: AvatarStroke[];
  updated_at?: string;
}

export interface ExperienceParticipant {
  id: string;
  user_id: string;
  experience_id: string;
  name: string;
  email: string;
  status: ParticipantStatus;
  role: ParticipantRole;
  segments?: string[];
  joined_at: string | null;
  fingerprint: string | null;
  created_at: string;
  updated_at: string;
  avatar?: AvatarData | null;
}

export interface BlockResponse {
  id: string;
  experience_participant_id: string;
  answer: any;
  created_at: string;
}

// Discriminated union for blocks
interface BaseBlock {
  id: string;
  status: BlockStatus;
  position: number;
  parent_block_id?: string | null;
  visible_to_roles?: ParticipantRole[];
  visible_to_segments?: string[];
  target_user_ids?: string[];
  show_in_lobby?: boolean;
  add_to_playbill?: boolean;
  playbill_mysterious?: boolean;
  sounds?: Partial<Record<string, SoundKey>>;
  created_at?: string;
  updated_at?: string;
  child_block_ids?: string[];
  parent_block_ids?: string[];
  children?: Block[];
  responses?: {
    total: number;
    user_responded?: boolean;
    user_response?: {
      id: string;
      answer: any;
    } | null;
    aggregate?: Record<string, number>;
    all_responses?: BlockResponse[];
  };
}

export interface PollBlock extends BaseBlock {
  kind: BlockKind.POLL;
  payload: PollPayload;
}

export interface QuestionBlock extends BaseBlock {
  kind: BlockKind.QUESTION;
  payload: QuestionPayload;
}

export interface AnnouncementBlock extends BaseBlock {
  kind: BlockKind.ANNOUNCEMENT;
  payload: AnnouncementPayload;
}

export interface FamilyFeudBlock extends BaseBlock {
  kind: BlockKind.FAMILY_FEUD;
  payload: FamilyFeudPayload;
}

export interface PhotoUploadBlock extends BaseBlock {
  kind: BlockKind.PHOTO_UPLOAD;
  payload: PhotoUploadPayload;
  responses?: {
    total: number;
    user_responded?: boolean;
    user_response?: {
      id: string;
      answer: JsonObject;
      photo_url?: string;
    } | null;
    all_responses?: Array<{
      id: string;
      experience_participant_id: string;
      answer: JsonObject;
      photo_url?: string;
      created_at: string;
    }>;
  };
}

export interface BuzzerBlock extends BaseBlock {
  kind: BlockKind.BUZZER;
  payload: BuzzerPayload;
  responses?: {
    total: number;
    user_responded?: boolean;
    user_response?: { id: string; answer: { buzzed_at: string } } | null;
    all_responses?: Array<{
      id: string;
      experience_participant_id: string;
      answer: { buzzed_at: string };
      created_at: string;
      avatar?: AvatarData | null;
    }>;
  };
}

export interface NewsletterSignupBlock extends BaseBlock {
  kind: BlockKind.NEWSLETTER_SIGNUP;
  payload: NewsletterSignupPayload;
  responses?: {
    total: number;
    subscribed_count?: number;
    all_responses?: Array<{
      id: string;
      experience_participant_id: string;
      answer: { subscribed?: boolean; submittedAt?: string };
      created_at: string;
    }>;
  };
}

export interface GuessWhoBlock extends BaseBlock {
  kind: BlockKind.GUESS_WHO;
  payload: GuessWhoPayload;
}

export interface MinigameArithmeticBlock extends BaseBlock {
  kind: BlockKind.MINIGAME_ARITHMETIC;
  payload: MinigameArithmeticPayload;
  responses?: {
    total: number;
    correct_count?: number;
    participant_counts?: Record<string, number>;
  };
}

export interface MinigameBalloonPumpBlock extends BaseBlock {
  kind: BlockKind.MINIGAME_BALLOON_PUMP;
  payload: MinigameBalloonPumpPayload;
  responses?: {
    total: number;
  };
}

export interface FeedbackBlock extends BaseBlock {
  kind: BlockKind.FEEDBACK;
  payload: FeedbackPayload;
  responses?: {
    total: number;
  };
}

export interface CollaborativeDrawingBlock extends BaseBlock {
  kind: BlockKind.COLLABORATIVE_DRAWING;
  payload: CollaborativeDrawingPayload;
  responses?: {
    total: number;
    assignment_count?: number;
    submission_count?: number;
    // Host-only (round block): the intake photos to pick from, and the current
    // selection.
    photos?: Array<{ id: string; photo_url?: string }>;
    selected_photo_ids?: string[];
  };
}

export interface TheSceneBlock extends BaseBlock {
  kind: BlockKind.THE_SCENE;
  payload: TheScenePayload;
  responses?: {
    total: number;
    vote_count?: number;
  };
}

export type Block =
  | PollBlock
  | QuestionBlock
  | AnnouncementBlock
  | FamilyFeudBlock
  | PhotoUploadBlock
  | BuzzerBlock
  | GuessWhoBlock
  | MinigameArithmeticBlock
  | MinigameBalloonPumpBlock
  | CollaborativeDrawingBlock
  | TheSceneBlock
  | FeedbackBlock
  | NewsletterSignupBlock;

export interface PlaybillRunningOrderEntry {
  id: string;
  kind: BlockKind;
  position: number;
  playbill_mysterious: boolean;
  title: string | null;
}

export interface Experience {
  id: string;
  name: string;
  code: string;
  code_slug: string;
  url: string;
  status: ExperienceStatus;
  description?: string;
  creator_id: string;
  hosts: ExperienceParticipant[];
  participants: ExperienceParticipant[];
  blocks: Block[];
  playbill_enabled?: boolean;
  playbill?: PlaybillSection[];
  playbill_running_order?: PlaybillRunningOrderEntry[];
  segments?: ExperienceSegment[];
  default_segment_id?: string | null;
  created_at: string;
  updated_at: string;
  participant_block_active?: boolean;
  responded_participant_ids?: string[];
}

// ===== TYPE FRAGMENTS (using Pick/Omit from complete types) =====

export type UserSummary = Pick<User, 'id' | 'name' | 'email'>;

export type ParticipantSummary = Pick<
  ExperienceParticipant,
  'id' | 'user_id' | 'name' | 'email' | 'role' | 'avatar' | 'segments'
>;

// ===== API REQUEST TYPES =====

export interface CreateExperienceRequest {
  experience: {
    name: string;
    code: string;
  };
}

export interface JoinExperienceRequest {
  code: string;
}

export interface RegisterExperienceRequest {
  email: string;
  name?: string;
  participant_name: string;
}

export interface CreateBlockPayload {
  kind: BlockKind;
  payload?:
    | PollPayload
    | QuestionPayload
    | AnnouncementPayload
    | FamilyFeudPayload
    | PhotoUploadPayload
    | BuzzerPayload
    | GuessWhoPayload
    | MinigameArithmeticPayload
    | MinigameBalloonPumpPayload
    | CollaborativeDrawingPayload
    | TheScenePayload
    | FeedbackPayload
    | NewsletterSignupPayload;
  visible_to_segment_ids?: string[];
  status?: BlockStatus;
  open_immediately?: boolean;
  add_to_playbill?: boolean;
  playbill_mysterious?: boolean;
  questions?: Array<{
    payload: Record<string, string | number | boolean>;
  }>;
}

export interface CreateExperienceBlockRequest {
  block: CreateBlockPayload;
}

// ===== API RESPONSE TYPES =====

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface ExperienceSummary {
  id: string;
  name: string;
  code_slug: string;
  status: ExperienceStatus;
  created_at: string;
}

export interface ExperiencesIndexResponse {
  experiences: ExperienceSummary[];
}

export interface CreateExperienceSuccessResponse {
  type: 'success';
  success: true;
  experience: {
    id: string;
    code: string;
    created_at: string;
    url: string;
  };
}

export interface CreateExperienceErrorResponse {
  type: 'error';
  success: false;
  message: string;
  error: string;
}

export type CreateExperienceApiResponse =
  | CreateExperienceSuccessResponse
  | CreateExperienceErrorResponse;

export interface GetExperienceSuccessResponse {
  type: 'success';
  success: true;
  experience: Experience;
  participant: ParticipantSummary | null;
}

export interface GetExperienceErrorResponse {
  type: 'error';
  error: string;
}

export type GetExperienceApiResponse = GetExperienceSuccessResponse | GetExperienceErrorResponse;

export interface JoinExperienceRegisteredResponse {
  type: 'success';
  jwt: string;
  url: string;
  status: 'registered';
  experience_name: string;
  experience_code_slug: string;
}

export interface JoinExperienceNeedsRegistrationResponse {
  type: 'needs_registration';
  experience_code: string;
  status: 'needs_registration';
  url: string;
  experience_name: string;
  experience_code_slug: string;
}

export interface JoinExperienceErrorResponse {
  type: 'error';
  error: string;
}

export type JoinExperienceApiResponse =
  | JoinExperienceRegisteredResponse
  | JoinExperienceNeedsRegistrationResponse
  | JoinExperienceErrorResponse;

export interface RegisterExperienceSuccessResponse {
  type: 'success';
  jwt: string;
  url: string;
  status: 'registered';
}

export interface RegisterExperienceErrorResponse {
  type: 'error';
  error: string;
}

export type RegisterExperienceApiResponse =
  | RegisterExperienceSuccessResponse
  | RegisterExperienceErrorResponse;

export type ExperienceOrchestrationResponse = ApiResponse<Experience>;

export type GetCurrentUserResponse = User;

// ===== AUTHENTICATION TYPES =====

export interface PasswordlessSignInRequest {
  passwordless: {
    email: string;
  };
}

export interface PasswordlessSignInResponse {
  // Typically returns HTML page or redirect
  // Success is indicated by redirect to intended page
}

export interface AuthenticationErrorResponse {
  error: string;
  message?: string;
}

export interface ExperienceJwtPayload {
  user_id: string;
  experience_id: string;
  exp: number;
  scope?: string;
}

// ===== ERROR TYPES =====

export interface ApiErrorResponse {
  error: string;
  message?: string;
  details?: Record<string, JsonValue>;
}

// ===== WEBSOCKET MESSAGE TYPES =====

export const WebSocketMessageTypes = {
  EXPERIENCE_STATE: 'experience_state',
  EXPERIENCE_UPDATED: 'experience_updated',
  STREAM_CHANGED: 'stream_changed',
  RESUBSCRIBE_REQUIRED: 'resubscribe_required',
  CONFIRM_SUBSCRIPTION: 'confirm_subscription',
  PING: 'ping',
  FAMILY_FEUD_UPDATED: 'family_feud_updated',
  SUBMISSION_STATE: 'submission_state',
  MINIGAME_BALLOON_PUMP_LEADER_UPDATED: 'minigame_balloon_pump_leader_updated',
} as const;

export type WebSocketMessageType =
  (typeof WebSocketMessageTypes)[keyof typeof WebSocketMessageTypes];

export type FamilyFeudDispatchPayload = FamilyFeudAction['payload'];

export interface FamilyFeudUpdatedMessageMetadata extends WebSocketMessageMetadata {
  block_id: string;
  operation: string;
  data: FamilyFeudDispatchPayload;
}

export type StreamType = 'role' | 'role_segments' | 'targeted' | 'profile';

export interface WebSocketMessageMetadata {
  participant_id: string;
  timestamp: number;
}

export interface ExperienceStateMessageMetadata extends WebSocketMessageMetadata {
  logical_stream: string;
}

export interface ExperienceUpdatedMessageMetadata extends WebSocketMessageMetadata {
  stream_key: string;
  stream_type: StreamType;
  role: ParticipantRole;
  segments: string[];
}

export interface StreamChangedMessageMetadata extends WebSocketMessageMetadata {
  old_stream: string;
  new_stream: string;
}

export interface ResubscribeRequiredMessageMetadata {
  participant_id: string;
  timestamp: number;
  reason: string;
}

// Base WebSocket message structure
export interface BaseWebSocketMessage<
  T extends WebSocketMessageType,
  M = WebSocketMessageMetadata,
> {
  type: T;
  metadata?: M;
}

// Experience-related messages that include experience data
export interface ExperienceWebSocketMessage<
  T extends WebSocketMessageType,
  M = WebSocketMessageMetadata,
> extends BaseWebSocketMessage<T, M> {
  experience: Experience;
  participant?: ParticipantSummary;
}

// Specific message types
export interface ExperienceStateMessage
  extends ExperienceWebSocketMessage<'experience_state', ExperienceStateMessageMetadata> {}

export interface ExperienceUpdatedMessage
  extends ExperienceWebSocketMessage<'experience_updated', ExperienceUpdatedMessageMetadata> {}

export interface StreamChangedMessage
  extends ExperienceWebSocketMessage<'stream_changed', StreamChangedMessageMetadata> {}

export interface ResubscribeRequiredMessage
  extends BaseWebSocketMessage<'resubscribe_required', ResubscribeRequiredMessageMetadata> {
  reason: string;
}

export interface FamilyFeudUpdatedMessage
  extends BaseWebSocketMessage<'family_feud_updated', FamilyFeudUpdatedMessageMetadata> {
  block_id: string;
  operation: string;
  data: FamilyFeudDispatchPayload;
}

export interface MinigameBalloonPumpLeaderUpdatedMessage
  extends BaseWebSocketMessage<'minigame_balloon_pump_leader_updated'> {
  block_id: string;
  leader_fill: number;
}

export type SubmissionState = Record<
  string,
  {
    id?: string;
    answer?: JsonObject;
    photo_url?: string;
    own_suggestion?: { id: string; text: string } | null;
    own_vote_suggestion_id?: string | null;
    current_question?: { index: number; prompt: string } | null;
    score?: { correct: number; completed: number };
    fill_amount?: number;
    assignment?: CollaborativeDrawingAssignment;
    image?: string | null;
    submitted?: boolean;
  }
>;

export interface SubmissionStateMessage {
  type: 'submission_state';
  submissions: SubmissionState;
}

export interface ConfirmSubscriptionMessage extends BaseWebSocketMessage<'confirm_subscription'> {}

export interface PingMessage extends BaseWebSocketMessage<'ping'> {}

// Union type for all possible WebSocket messages
export type WebSocketMessage =
  | ExperienceStateMessage
  | ExperienceUpdatedMessage
  | StreamChangedMessage
  | ResubscribeRequiredMessage
  | ConfirmSubscriptionMessage
  | PingMessage
  | FamilyFeudUpdatedMessage
  | SubmissionStateMessage
  | MinigameBalloonPumpLeaderUpdatedMessage;

export interface DrawingUpdateMessage {
  type: 'drawing_update';
  participant_id: string;
  operation: string;
  data?: unknown;
}

export type ExperiencePayloadMessage =
  | ExperienceStateMessage
  | ExperienceUpdatedMessage
  | StreamChangedMessage;

export type ExperienceChannelMessage = WebSocketMessage | DrawingUpdateMessage;

export function isExperiencePayloadMessage(msg: WebSocketMessage): msg is ExperiencePayloadMessage {
  return (
    msg.type === WebSocketMessageTypes.EXPERIENCE_STATE ||
    msg.type === WebSocketMessageTypes.EXPERIENCE_UPDATED ||
    msg.type === WebSocketMessageTypes.STREAM_CHANGED
  );
}

export function isDrawingUpdateMessage(msg: ExperienceChannelMessage): msg is DrawingUpdateMessage {
  return msg.type === 'drawing_update';
}

// ===== EVENT & PERFORMER TYPES =====

export interface Performer {
  id: string;
  user_id?: string;
  name: string;
  bio: string | null;
  slug: string;
  photo_url: string | null;
  follower_count: number;
  followed_by_current_user: boolean;
  editable_by_current_user: boolean;
  upcoming_events?: EventSummary[];
}

export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  venue_name: string | null;
  venue_address: string | null;
  pricing_text: string | null;
  ticket_url: string | null;
  slug: string;
  published: boolean;
  performers: Pick<Performer, 'id' | 'name' | 'slug' | 'photo_url' | 'followed_by_current_user'>[];
  experience: {
    code_slug: string;
    status: ExperienceStatus;
    active: boolean;
  } | null;
  created_at?: string;
  updated_at?: string;
}

export type EventSummary = Pick<
  CalendarEvent,
  'id' | 'title' | 'starts_at' | 'ends_at' | 'slug' | 'venue_name'
>;

export interface EventsApiResponse {
  type: 'success';
  success: true;
  events: CalendarEvent[];
}

export interface EventApiResponse {
  type: 'success';
  success: true;
  event: CalendarEvent;
}

export interface DiscoverTheater {
  slug: string;
  name: string;
  neighborhood: string;
  lat: number;
  lng: number;
}

export interface DiscoverEvent {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  venue_name: string | null;
  venue_address: string | null;
  pricing_text: string | null;
  ticket_url: string | null;
  slug: string;
  performers: Pick<Performer, 'id' | 'name' | 'slug'>[];
  theater_slug: string | null;
  is_live: boolean;
}

export interface DiscoverApiResponse {
  type: 'success';
  success: true;
  theaters: DiscoverTheater[];
  events: DiscoverEvent[];
}

export interface PerformersApiResponse {
  type: 'success';
  success: true;
  performers: Performer[];
}

export interface PerformerApiResponse {
  type: 'success';
  success: true;
  performer: Performer;
}

// ===== CREATE EXPERIENCE FORM TYPES =====

// Base data types for each block type (form state)
export interface PollData {
  question: string;
  options: string[];
  pollType: 'single' | 'multiple';
  segmentAssignments: Record<string, string>;
}

export interface QuestionData {
  questionText: string;
  questionFormKey: string;
  questionInputType: 'text' | 'number' | 'email' | 'password' | 'tel';
}

export interface AnnouncementData {
  message: string;
  show_on_monitor: boolean;
}

export interface FamilyFeudData {
  title: string;
  questions: Array<{
    id: string;
    question: string;
    synthetic?: boolean;
  }>;
}

export interface PhotoUploadData {
  prompt: string;
}

export interface BuzzerData {
  label: string;
  prompt: string;
}

export interface NewsletterSignupData {
  prompt: string;
  confirmationMessage: string;
  declineMessage: string;
}

export interface GuessWhoData {
  segment_id: string;
}

export interface MinigameArithmeticData {
  duration_seconds: number;
  question_count: number;
  leaderboard_size: number;
}

export interface MinigameBalloonPumpData {
  target_units: number;
}

export interface CollaborativeDrawingData {
  prompt: string;
  min_subsections: number;
  max_subsections: number;
  drawing_time_seconds: number;
  total_drawings: number;
}

export interface TheSceneData {
  leaderboard_size: number;
  prompt_input_count: number;
  performer_participant_ids: string[];
}

export interface FeedbackData {
  prompt: string;
  allowed_types: FeedbackType[];
  require_title: boolean;
}

// Union type for all block component data
export type BlockComponentData =
  | PollData
  | QuestionData
  | AnnouncementData
  | FamilyFeudData
  | PhotoUploadData
  | BuzzerData
  | GuessWhoData
  | MinigameArithmeticData
  | MinigameBalloonPumpData
  | CollaborativeDrawingData
  | TheSceneData
  | FeedbackData
  | NewsletterSignupData;

// Discriminated union for form block data
export type FormBlockData =
  | { kind: BlockKind.POLL; data: PollData }
  | { kind: BlockKind.QUESTION; data: QuestionData }
  | { kind: BlockKind.ANNOUNCEMENT; data: AnnouncementData }
  | { kind: BlockKind.FAMILY_FEUD; data: FamilyFeudData }
  | { kind: BlockKind.PHOTO_UPLOAD; data: PhotoUploadData }
  | { kind: BlockKind.BUZZER; data: BuzzerData }
  | { kind: BlockKind.GUESS_WHO; data: GuessWhoData }
  | { kind: BlockKind.MINIGAME_ARITHMETIC; data: MinigameArithmeticData }
  | { kind: BlockKind.MINIGAME_BALLOON_PUMP; data: MinigameBalloonPumpData }
  | { kind: BlockKind.COLLABORATIVE_DRAWING; data: CollaborativeDrawingData }
  | { kind: BlockKind.THE_SCENE; data: TheSceneData }
  | { kind: BlockKind.FEEDBACK; data: FeedbackData }
  | { kind: BlockKind.NEWSLETTER_SIGNUP; data: NewsletterSignupData };

export interface UpdateBlockPayload {
  payload: ApiPayload | JsonObject;
  visible_to_segment_ids: string[];
  questions?: Array<{ id: string; question: string }>;
}

export interface EditBlockContextValue {
  blockData: FormBlockData;
  setBlockData: (data: FormBlockData | ((prev: FormBlockData) => FormBlockData)) => void;
  participants: ParticipantSummary[];
  submit: () => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  visibleSegments: string[];
  setVisibleSegments: (segments: string[]) => void;
  viewAdditionalDetails: boolean;
  setViewAdditionalDetails: (view: boolean) => void;
  showOnMonitor: boolean;
  setShowOnMonitor: (value: boolean) => void;
  pendingWarning: string | null;
  confirmWarning: () => void;
  cancelWarning: () => void;
}

export interface CreateBlockContextValue {
  // Form block data with discriminated union
  blockData: FormBlockData;
  setBlockData: (data: FormBlockData | ((prev: FormBlockData) => FormBlockData)) => void;
  setKind: (kind: BlockKind) => void;

  // Participants
  participants: ParticipantSummary[];

  // Form submission
  submit: (status: BlockStatus) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;

  // Visibility — segments default to the experience's default segment.
  visibleSegments: string[];
  setVisibleSegments: (segments: string[]) => void;
  viewAdditionalDetails: boolean;
  setViewAdditionalDetails: (view: boolean) => void;

  addToPlaybill: boolean;
  setAddToPlaybill: (value: boolean) => void;
  playbillMysterious: boolean;
  setPlaybillMysterious: (value: boolean) => void;
}

// Props interface for block components
export interface BlockComponentProps<T = BlockComponentData> {
  data: T;
  onChange?: (data: Partial<T>) => void;
  participants?: ParticipantSummary[];
}

// ===== CONTEXT TYPES =====

export interface ExperienceContextType {
  experience?: Experience;
  participant?: ParticipantSummary;
  code: string;
  jwt?: string;

  isAuthenticated: boolean;
  isLoading: boolean;
  experienceStatus: 'lobby' | 'live';
  error?: string;

  setParticipantJWT: (token: string) => void;
  clearAuth: () => void;
  experienceFetch: (url: string, options?: RequestInit) => Promise<Response>;

  wsConnected: boolean;
  wsReady: boolean;
  wsError?: string;

  // Manage page specific properties
  monitorView?: Experience;
  participantView?: Experience;
  impersonatedParticipantId?: string;
  setImpersonatedParticipantId: (id: string | undefined) => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerFamilyFeudDispatch?: (blockId: string, dispatch: (action: any) => void) => void;
  unregisterFamilyFeudDispatch?: (blockId: string) => void;

  experiencePerform?: (
    action: string,
    payload?: JsonObject,
    target?: 'primary' | 'monitor' | 'impersonation',
  ) => void;
}
